/**
 * Decimal-degree coordinate parsing for the map dialog's search field.
 *
 * Suppliers routinely copy a GPS reading off their phone ("9.1, 38.7") and paste it into a
 * search box. Mapbox's geocoder only understands place names, so the parsed candidates are fed
 * back into it through its `localGeocoder` hook as synthetic Carmen GeoJSON features.
 *
 * Deliberately decimal degrees only — DMS ("48°08'13.2\"N") and hemisphere suffixes are out of
 * scope, because phones hand out decimal degrees and every extra format widens the ambiguity
 * surface below.
 */

/** A single interpretation of a coordinate query. */
export interface CoordinateCandidate {
  lat: number;
  lng: number;
}

/**
 * The subset of the Carmen GeoJSON shape the geocoder needs for a local result.
 *
 * Note the missing `bbox`: without one the geocoder flies to its configured `zoom` instead of
 * fitting bounds, which is what we want for a point.
 */
export interface CoordinateSearchResult {
  id: string;
  type: 'Feature';
  place_type: ['coordinate'];
  place_name: string;
  text: string;
  center: [number, number];
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: Record<string, never>;
  relevance: number;
}

const SEPARATOR = /[\s,;]+/;
/** Plain decimal degrees; exponent notation and unit/hemisphere suffixes are rejected. */
const DECIMAL_DEGREES = /^[+-]?(\d+(\.\d+)?|\.\d+)$/;

const MAX_LAT = 90;
const MAX_LNG = 180;

const isLat = (n: number) => Number.isFinite(n) && Math.abs(n) <= MAX_LAT;
const isLng = (n: number) => Number.isFinite(n) && Math.abs(n) <= MAX_LNG;

/**
 * Parse a search query into the coordinate readings it could plausibly mean.
 *
 * Returns an empty array for anything that is not a pair of decimal degrees. When both
 * orderings are valid and distinct (e.g. `9.1, 38.7`) both are returned, literal reading first —
 * the user picks. This mirrors Mapbox's own coordinate-search example.
 */
export function parseCoordinateQuery(query: string): CoordinateCandidate[] {
  if (typeof query !== 'string') return [];

  const parts = query.trim().split(SEPARATOR).filter(Boolean);
  if (parts.length !== 2) return [];
  if (!parts.every((p) => DECIMAL_DEGREES.test(p))) return [];

  const [a, b] = parts.map(Number);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return [];

  const candidates: CoordinateCandidate[] = [];
  if (isLat(a) && isLng(b)) candidates.push({ lat: a, lng: b });
  if (a !== b && isLat(b) && isLng(a)) candidates.push({ lat: b, lng: a });
  return candidates;
}

/** `48.1370` and `48.137` label identically — the parsed number is the source of truth. */
const format = (n: number) => String(n);

function toSearchResult(candidate: CoordinateCandidate): CoordinateSearchResult {
  const { lat, lng } = candidate;
  const label = `Lat ${format(lat)}, Lng ${format(lng)}`;
  return {
    id: `coordinate.${format(lat)},${format(lng)}`,
    type: 'Feature',
    place_type: ['coordinate'],
    place_name: label,
    text: label,
    center: [lng, lat],
    geometry: { type: 'Point', coordinates: [lng, lat] },
    properties: {},
    relevance: 1,
  };
}

/**
 * Geocoder `localGeocoder` hook: turn a coordinate query into result features.
 *
 * The geocoder prepends these to the Mapbox place results, so a coordinate paste always ranks
 * above any place that happens to share the digits.
 */
export function coordinateSearchResults(query: string): CoordinateSearchResult[] {
  return parseCoordinateQuery(query).map(toSearchResult);
}
